import React, { useState } from "react";
import { Table, TableProps } from "antd";
import type { TablePaginationConfig } from "antd/es/table";
import { number } from "yup";
import { TableSortTypes } from "../../enums/ViewListingTypes";

interface CustomTableWithPaginationProps<T> extends TableProps<T> {
  showPagination?: boolean;
  data: T[];
  columns: any[];
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  showSizeChanger?: boolean;
  handleSelectedRow?: (record: T, index?: number) => void;
  handleSingleSelect?: (record: T, index?: number) => void;
  bordered?: boolean;
  isClickableTable?: boolean;
  scroll?: { x?: number | string; y?: number | string };
  handleChange?: (page: number) => void;
  totalData?: number;
  handleSizeChangerChange?: (size: number) => void;
  pageSize?: number;
  currentPage?: number;
  rowSelection?: object;
  rowKey?: (record: T) => string | string;
  loading?: boolean;
  isSortable?: boolean;
  handleSorter?: (sorter) => void;
}

const CustomTableWithPagination = <T extends object>({
  showPagination = true,
  data,
  columns,
  pageSizeOptions = [10, 25, 50, 100],
  defaultPageSize = 10,
  showSizeChanger = true,
  scroll = { x: "max-content" },
  itemName = "items",
  bordered,
  handleSelectedRow,
  handleSingleSelect,
  isClickableTable = false,
  handleChange,
  totalData,
  handleSizeChangerChange,
  pageSize,
  currentPage,
  loading,
  isSortable,
  handleSorter,
  ...rest
}: CustomTableWithPaginationProps<T>) => {
  const paginationConfig: TablePaginationConfig = {
    current: currentPage,
    pageSize: pageSize,
    pageSizeOptions,
    showSizeChanger,
    total: totalData,
    onChange: handleChange,
    onShowSizeChange: (current, size) => {
      handleSizeChangerChange && handleSizeChangerChange(size);
    },
    showTotal: (total, range) => (
      <div className="pagination-total">
        {`${range[0]}-${range[1]} of ${total} ${itemName}`}
      </div>
    ),
  };

  return (
    <Table
      onRow={(record, index) => {
        return {
          onDoubleClick: (e: React.MouseEvent<HTMLElement>) => {
            const target = e.target as HTMLElement;
            //ignore checkbox clicks
            if (
              target.querySelector(".ant-checkbox") ||
              target.closest(".ant-checkbox")
            ) {
              return;
            }
            e.stopPropagation();
            if (isClickableTable) {
              handleSelectedRow && handleSelectedRow(record, index);
            }
          },
          // onSelect: () => handleSelectedRow && handleSelectedRow(record, index),
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            const target = e.target as HTMLElement;
            if (
              target.querySelector(".ant-checkbox") ||
              target.closest(".ant-checkbox")
            ) {
              return;
            }
            e.stopPropagation();
            if (isClickableTable) {
              handleSingleSelect && handleSingleSelect(record, index);
            }
          },
        };
      }}
      columns={columns}
      dataSource={data}
      pagination={showPagination ? paginationConfig : showPagination}
      scroll={scroll}
      bordered={bordered}
      loading={loading}
      onChange={(pagination, filter, sorter, extra) => {
        if (isSortable) {
          const sortOrder = !!!sorter?.order
            ? undefined
            : sorter?.order === "ascend"
            ? TableSortTypes.ASCEND
            : TableSortTypes.DESCEND;
          handleSorter?.({ ...sorter, order: sortOrder });
        }
      }}
      {...rest}
    />
  );
};

export default CustomTableWithPagination;
